import { useMemo } from "react";

import Tile from "ds/components/Tile";
import TooltipInfo from "ds/components/TooltipInfo";
import useTimeSettingsStore from "hooks/useTimeSettingsStore";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import FormToggleField from "ds/components/Form/ToggleField";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";
import PageInfoHeader from "ds/components/PageInfoHeader";

const DateTimeSettings = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { timeFormat, setTimeFormat, getResolvedTimeFormat } = useTimeSettingsStore();

  useTitle(`Personal Settings · Time Preferences · ${accountName}`);

  const currentTimezone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, []);

  const isSystemMode = timeFormat === "system";
  const resolvedTimeFormat = getResolvedTimeFormat();

  const toggleSystemMode = (isSelected: boolean) => {
    if (isSelected) {
      setTimeFormat("system");
    } else {
      setTimeFormat(resolvedTimeFormat);
    }
  };

  const handleTimeFormatChange = (format: "12" | "24") => {
    setTimeFormat(format);
  };

  return (
    <>
      <PageInfoHeader title="Time preferences" />

      <Box direction="column" grow="1" padding="x-large" limitWidth="medium">
        <Box direction="column" gap="x-large" fullWidth>
          <Box direction="column" gap="small">
            <Typography variant="p-t5" tag="h3">
              Timezone
            </Typography>

            <Box direction="column" gap="large">
              <Typography variant="p-body2" tag="p" color="secondary">
                Your current timezone is automatically detected from your system settings.
              </Typography>

              <Tile title={currentTimezone} />
            </Box>
          </Box>

          <Box direction="column" gap="small">
            <Typography variant="p-t5" tag="h3">
              Time format
            </Typography>

            <Box direction="column" gap="large">
              <Box direction="row" gap="small" align="center">
                <Typography variant="p-body2" tag="p" color="secondary">
                  Choose how time should be displayed throughout the application.
                </Typography>
                <TooltipInfo variant="modal">
                  This setting only affects time display in the interface. Log timestamps (like Run
                  logs) show the original format from remote machines and are not affected by this
                  preference.
                </TooltipInfo>
              </Box>
              <FormToggleField
                variant="switch"
                onChange={toggleSystemMode}
                checked={isSystemMode}
                title="Use system format"
                description={`Follow system preferences for time format.`}
              />

              <Box direction="column" gap="small">
                <Box direction="row" gap="medium" fullWidth>
                  <Tile
                    title="12-hour format"
                    description="2:30:45 PM"
                    selected={resolvedTimeFormat === "12"}
                    disabled={isSystemMode}
                    indicator="radio"
                    onClick={() => !isSystemMode && handleTimeFormatChange("12")}
                  />

                  <Tile
                    title="24-hour format"
                    description="14:30:45"
                    selected={resolvedTimeFormat === "24"}
                    disabled={isSystemMode}
                    indicator="radio"
                    onClick={() => !isSystemMode && handleTimeFormatChange("24")}
                  />
                </Box>

                {isSystemMode && (
                  <Typography variant="p-body3" tag="p" color="secondary">
                    {resolvedTimeFormat === "12" ? "12-hour" : "24-hour"} format detected from your
                    browser language setting: {navigator.language}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DateTimeSettings;
